import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import Accordion from 'components/common/accordion'
import { PDP_INTERACT_ELEM_EVENT_NAME } from 'components/pdp/const'
import { Modal } from 'components/portal/modal'
import useModal from 'components/portal/useModal'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { segmentTrackPDPInteract } from 'lib/utils/product'
import ProductSizeGuideDialogs from 'components/shared/product/product-size-guide-dialogs/ProductSizeGuideDialogs'
import { MEASUREMENT_DATA } from './const'
import ModelInfo from './model-info'
import SizesGuideModal from './sizes-guide'
import DoubleTableSection from './table-section/double'
import SingleTableSection from './table-section/single'

const ProductMeasurements = ({
  isExpandedAccordion,
  onOpenProductMeasurements,
  internationalization: { country },
  product,
  t,
}) => {
  const [isOpen, setIsOpen] = useModal(false)
  const {
    attributes,
    is_new_size_method: isNewSize = false,
    measurement_spec,
    model,
  } = product
  const updatedProductSizes = attributes
    ?.filter((attribue) => attribue?.is_measurement_updated)
    ?.map((attribue) => attribue?.size)

  const addedProductSizes = attributes
    ?.filter((attribue) => attribue?.is_new_measurement_added)
    ?.map((attribue) => attribue?.size)

  const isProductSizesUpdated = updatedProductSizes?.length > 0
  const isProductSizesAdded = addedProductSizes?.length > 0

  const shoeDualTableFlagValue = true // 'NewShoeSizeGuide-20221031'

  const sizeGuideTitle = hasLocizeTranslation(
    t,
    MEASUREMENT_DATA.sizeGuideTitle.titleKey,
    MEASUREMENT_DATA.sizeGuideTitle.default,
  )

  const tableProps = {
    openModal: () => setIsOpen(true),
    product,
  }

  // measurementy_type_body - PMLO's new dual table policy field
  const isPmloDualTable = !!attributes?.[0]?.measurement_type_body?.length

  // measurement_spec - Shoe category dual table policy
  const isMeasurementSpecValid = measurement_spec?.some(
    (item) => item.is_fix_unit,
  )
  const isShoeDualTable = shoeDualTableFlagValue && isMeasurementSpecValid

  const shouldDisplayDualTable = isPmloDualTable || isShoeDualTable

  return (
    <Fragment>
      <Accordion
        isExpanded={isExpandedAccordion}
        title={sizeGuideTitle}
        isSizeGuide={isNewSize || isProductSizesAdded}
        isSizeUpdated={isProductSizesUpdated}
        onChange={(isAccordionOpen) => {
          onOpenProductMeasurements(isAccordionOpen)
          return (
            isAccordionOpen &&
            segmentTrackPDPInteract(
              product,
              country,
              PDP_INTERACT_ELEM_EVENT_NAME.sizeGuide,
            )
          )
        }}
      >
        <React.Fragment>
          {(isProductSizesUpdated || isProductSizesAdded) && (
            <ProductSizeGuideDialogs
              t={t}
              isSizeUpdated={isProductSizesUpdated}
              isSizeAdded={isProductSizesAdded}
              updatedSizes={updatedProductSizes}
              addedSizes={addedProductSizes}
            />
          )}
          {shouldDisplayDualTable ? (
            <DoubleTableSection
              isPmloDualTable={isPmloDualTable}
              isShoeDualTable={isShoeDualTable}
              {...tableProps}
            />
          ) : (
            <SingleTableSection {...tableProps} />
          )}
          {!shouldDisplayDualTable && model && <ModelInfo model={model} />}
        </React.Fragment>
      </Accordion>
      <Modal isOpen={isOpen} disabledPointerEvents>
        <SizesGuideModal
          onClose={() => setIsOpen(false)}
          isNewSize={isNewSize}
        />
      </Modal>
    </Fragment>
  )
}

ProductMeasurements.defaultProps = {
  isExpandedAccordion: false,
}

ProductMeasurements.propTypes = {
  // component props
  onOpenProductMeasurements: PropTypes.func.isRequired,
  isExpandedAccordion: PropTypes.bool,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
  }).isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        measurements: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
            value_in: PropTypes.string,
          }),
        ),
        measurement_type_body: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
            value_in: PropTypes.string,
          }),
        ),
      }),
    ),
    category: PropTypes.shape({
      id_category: PropTypes.number,
      link_rewrite: PropTypes.string,
    }),
    id_product: PropTypes.number,
    is_new_size_method: PropTypes.bool,
    measurement_image: PropTypes.shape({
      path: PropTypes.string,
    }),
    measurement_spec: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        is_fix_unit: PropTypes.bool,
      }),
    ),
    measurement_type: PropTypes.string,
    metadata: PropTypes.shape({
      size_recommendable: PropTypes.bool,
    }),
    model: PropTypes.shape({
      bust: PropTypes.number,
      height: PropTypes.number,
      hips: PropTypes.number,
      size: PropTypes.string,
      waist: PropTypes.number,
    }),
    prices: PropTypes.shape({
      original_price: PropTypes.number,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
}

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
  })),
  withI18next(),
)(ProductMeasurements)
